import { plainToInstance } from 'class-transformer'

import {
  type RequestPaymentPathVar,
  type ParkinglotRequestPaymentPayload,
  type RequestPaymentAuth,
  type SharedParkinglotRequestPaymentPayload,
  type ExtensionSharedParkinglotRequestPaymentPayload,
  type ShareTicketAbleTimePathVar,
  type RequestAuth,
  ShareTicketAbleTimeResDto,
  RequestAuthCodeResDto,
  RequestAuthGuestResDto,
  PaymentResDto,
  TicketAbleTimePathVar,
  TicketDailyAbleTimeResDto,
  TicketMonthlyAbleTimeResDto,
  RequestAuthGuestPayload,
  RequestVerifyCodePayload,
  RequestAuthCodePayload
} from '@/api/client/purchase/dto/purchaseDto'
import axios from '@/lib/axiosClient'

export const purchaseUrlEndpoint = '/user'
export const sharePurchaseUrlEndpoint = '/ticket/share'
export const paymentUrlEndpoint = '/ticket'

/** 결제 인증 */
/**
 * 인증코드재요청
 * @payload {RequestVerifyCode}
 */
export const requestAuthCode = async (payload: RequestAuthCodePayload): Promise<RequestAuthCodeResDto> => {
  const { data } = await axios.post(`${purchaseUrlEndpoint}/code/send`, payload)
  return plainToInstance(RequestAuthCodeResDto, data)
}

/**
 * 인증코드요청
 * @payload {RequestVerifyCode}
 */
export const requestVerifyCode = async (payload: RequestVerifyCodePayload) => {
  await axios.post(`${purchaseUrlEndpoint}/code/verify`, payload)
}

/**
 * 결제요청
 * @pathVar {pgType: string}
 * @auth {token: string}
 * @payload {ParkinglotRequestPaymentPayload}
 */
export const requestAuthGuest = async (payload: RequestAuthGuestPayload): Promise<RequestAuthGuestResDto> => {
  const { data } = await axios.post(`${purchaseUrlEndpoint}/login/guest`, payload)
  return plainToInstance(RequestAuthGuestResDto, data)
}

/** 제휴주차장 */
/**
 * 결제요청
 * @pathVar {pgType: string}
 * @auth {token: string}
 * @payload {ParkinglotRequestPaymentPayload}
 */
export const requestParkinglotPayment = async (
  pathVar: RequestPaymentPathVar,
  payload: ParkinglotRequestPaymentPayload,
  auth: RequestPaymentAuth
): Promise<PaymentResDto> => {
  const headers = {
    Authorization: `Bearer ${auth.token}`
  }
  const { data } = await axios.post(`${paymentUrlEndpoint}/payment/webview/${pathVar.pgType}`, payload, { headers })
  return plainToInstance(PaymentResDto, data)
}

/**
 * 일정기 시간대 목록
 * @pathVar {seq: number}
 * @return {data: AbleTime[]}
 */
export const getTicketDailyAbleTime = async (pathVar: TicketAbleTimePathVar) => {
  const { data } = await axios.get(`${paymentUrlEndpoint}/${pathVar.seq}/daily-able-time`)
  return plainToInstance(TicketDailyAbleTimeResDto, data)
}

/**
 * 월정기 시간대 목록
 * @pathVar {seq: number}
 * @return {data: TicketMonthlyAbleTimeResDto['data']}
 */
export const getMonthlyTicketDailyAbleTime = async (pathVar: TicketAbleTimePathVar) => {
  const { data } = await axios.get(`${paymentUrlEndpoint}/${pathVar.seq}/monthly-able-date`)
  return plainToInstance(TicketMonthlyAbleTimeResDto, data)
}

/**
 * 월정기 시간대 목록
 * @pathVar {seq: number}
 * @return {data: ShareTicketAbleTimeResDto['data']}
 */
export const getShareTicketDailyAbleTime = async (
  pathVar: Omit<ShareTicketAbleTimePathVar, 'endTime'>,
  auth: RequestAuth
) => {
  const headers = {
    Authorization: `Bearer ${auth.accessToken}`
  }
  const { data } = await axios.get(`${sharePurchaseUrlEndpoint}/${pathVar.seq}/time`, {
    headers
  })
  return plainToInstance(ShareTicketAbleTimeResDto, data)
}

/**
 * 공유주차장 연장시간대 목록
 * @pathVar {seq: number,endTime: Date}
 * @auth {accessToken: string}
 * @return {data: ShareTicketAbleTimeResDto['data']}
 */
export const getShareTicketExtendDailyAbleTime = async (pathVar: ShareTicketAbleTimePathVar, auth: RequestAuth) => {
  const headers = {
    Authorization: `Bearer ${auth.accessToken}`
  }
  const { data } = await axios.get(`${sharePurchaseUrlEndpoint}/${pathVar.seq}/time?endTime=${pathVar.endTime}`, {
    headers
  })
  return plainToInstance(ShareTicketAbleTimeResDto, data)
}

/** 공유주차장 */
/**
 * 결제요청
 * @pathVar {pgType: string}
 * @auth {token: string}
 * @payload {ParkinglotRequestPaymentPayload}
 */
export const requestSharedParkinglotPayment = async (
  pathVar: RequestPaymentPathVar,
  payload: SharedParkinglotRequestPaymentPayload,
  auth: RequestPaymentAuth
): Promise<PaymentResDto> => {
  const headers = {
    Authorization: `Bearer ${auth.token}`
  }
  const { data } = await axios.post(`${paymentUrlEndpoint}/payment/share/webview/${pathVar.pgType}`, payload, {
    headers
  })
  return plainToInstance(PaymentResDto, data)
}

/** 공유주차장 연장결제 */
/**
 * 결제요청
 * @pathVar {pgType: string}
 * @auth {token: string}
 * @payload {ParkinglotRequestPaymentPayload}
 */
export const requestExtensionSharedParkinglotPayment = async (
  pathVar: RequestPaymentPathVar,
  payload: ExtensionSharedParkinglotRequestPaymentPayload,
  auth: RequestPaymentAuth
): Promise<PaymentResDto> => {
  const headers = {
    Authorization: `Bearer ${auth.token}`
  }
  const { data } = await axios.post(
    `${paymentUrlEndpoint}/payment/share/extension/webview/${pathVar.pgType}`,
    payload,
    {
      headers
    }
  )
  return plainToInstance(PaymentResDto, data)
}
